import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Home, Receipt, Target, User, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import EmailVerificationBanner from "@/components/EmailVerificationBanner";
import PaywallOverlay from "@/components/PaywallOverlay";

const navItems = [
  { to: "/home", icon: Home, label: "Home" },
  { to: "/transactions", icon: Receipt, label: "Lançamentos" },
  { to: "/pente-fino", icon: Search, label: "Pente Fino" },
  { to: "/goals", icon: Target, label: "Metas" },
  { to: "/profile", icon: User, label: "Perfil" },
];

export function Layout() {
  const location = useLocation();
  const { isExpired } = useAuth();

  // Páginas que NÃO devem ser bloqueadas pelo paywall (usuário expirado precisa pagar!)
  const isPaymentPage = location.pathname === "/profile/payment";

  return (
    <div className="mobile-container">
      {/* Email Verification Banner */}
      <EmailVerificationBanner />
      
      {/* Paywall Overlay - blocks access when trial expired (exceto página de pagamento) */}
      <PaywallOverlay isOpen={isExpired && !isPaymentPage} />
      
      {/* Main Content Area */}
      <main className="min-h-screen pb-20">
        <Outlet />
      </main>

      {/* Bottom Navigation Bar */}
      <nav 
        data-testid="bottom-nav"
        className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white/95 backdrop-blur-md border-t border-slate-200 h-[calc(4rem+env(safe-area-inset-bottom))] pb-[env(safe-area-inset-bottom)] flex items-center justify-around z-50 px-2"
      >
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`nav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              cn(
                "flex flex-col items-center justify-center gap-1 px-2 py-2 rounded-full transition-all duration-200 relative no-select",
                isActive
                  ? "text-primary font-bold"
                  : "text-muted-foreground hover:text-foreground"
              )
            }
          >
            {({ isActive }) => (
              <>
                <motion.div
                  animate={isActive ? { scale: 1.1 } : { scale: 1 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                >
                  <Icon className="w-5 h-5" strokeWidth={isActive ? 2.5 : 2} />
                </motion.div>
                <span translate="no" className="text-[10px] font-medium">{label}</span>
                {isActive && (
                  <motion.span
                    layoutId="nav-indicator"
                    className="absolute -bottom-1 w-1 h-1 bg-primary rounded-full"
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
